import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  FlaskConical,
  Play,
  Pause,
  Trophy,
  Trash2,
  MoreVertical,
  BarChart3,
  Palette,
  Link2,
  Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { ABTest, ABTestStatus, ABTestVariant } from '@/hooks/useABTests';

interface ABTestCardProps {
  test: ABTest & { variants?: ABTestVariant[] };
  onViewAnalytics: (testId: string) => void;
  onUpdateStatus: (testId: string, status: ABTestStatus) => void;
  onDeclareWinner: (testId: string) => void;
  onDelete: (testId: string) => void;
}

const statusConfig: Record<ABTestStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-muted text-muted-foreground' },
  active: { label: 'Running', color: 'bg-green-500/10 text-green-600' },
  paused: { label: 'Paused', color: 'bg-yellow-500/10 text-yellow-600' },
  completed: { label: 'Completed', color: 'bg-blue-500/10 text-blue-600' },
};

export function ABTestCard({
  test,
  onViewAnalytics,
  onUpdateStatus,
  onDeclareWinner,
  onDelete,
}: ABTestCardProps) {
  const totalScans = test.variants?.reduce((sum, v) => sum + v.scanCount, 0) || 0;
  const leadingVariant = test.variants?.reduce(
    (max, v) => (v.scanCount > (max?.scanCount || 0) ? v : max),
    test.variants[0]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
    >
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">{test.name}</h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {test.testType === 'design' ? (
                  <Palette className="h-3.5 w-3.5" />
                ) : (
                  <Link2 className="h-3.5 w-3.5" />
                )}
                <span>{test.testType === 'design' ? 'Design Test' : 'Destination Test'}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={cn('text-xs', statusConfig[test.status].color)}>
                {statusConfig[test.status].label}
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onViewAnalytics(test.id)}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    View Analytics
                  </DropdownMenuItem>
                  {test.status === 'draft' && (
                    <DropdownMenuItem onClick={() => onUpdateStatus(test.id, 'active')}>
                      <Play className="h-4 w-4 mr-2" />
                      Start Test
                    </DropdownMenuItem>
                  )}
                  {test.status === 'active' && (
                    <DropdownMenuItem onClick={() => onUpdateStatus(test.id, 'paused')}>
                      <Pause className="h-4 w-4 mr-2" />
                      Pause Test
                    </DropdownMenuItem>
                  )}
                  {test.status === 'paused' && (
                    <DropdownMenuItem onClick={() => onUpdateStatus(test.id, 'active')}>
                      <Play className="h-4 w-4 mr-2" />
                      Resume Test
                    </DropdownMenuItem>
                  )}
                  {(test.status === 'active' || test.status === 'paused') && (
                    <DropdownMenuItem onClick={() => onDeclareWinner(test.id)}>
                      <Trophy className="h-4 w-4 mr-2" />
                      Declare Winner
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(test.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Test
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Variant progress bars */}
          <div className="space-y-3">
            {test.variants?.map((variant) => {
              const percentage = totalScans > 0 ? (variant.scanCount / totalScans) * 100 : 0;
              const isWinner = test.winnerVariant === variant.variantName;
              const isLeading = variant.id === leadingVariant?.id && totalScans > 0;

              return (
                <div key={variant.id} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="font-medium">Variant {variant.variantName}</span>
                      {isWinner && (
                        <Badge variant="default" className="text-xs">
                          <Trophy className="h-3 w-3 mr-1" />
                          Winner
                        </Badge>
                      )}
                      {!isWinner && isLeading && test.status !== 'completed' && (
                        <Badge variant="secondary" className="text-xs">
                          Leading
                        </Badge>
                      )}
                    </span>
                    <span className="text-muted-foreground">
                      {variant.scanCount} scans ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <Progress
                    value={percentage}
                    className={cn(
                      'h-2',
                      isWinner && '[&>div]:bg-green-500'
                    )}
                  />
                </div>
              );
            })}
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-between pt-2 border-t text-sm">
            <div className="flex items-center gap-4 text-muted-foreground">
              <span>{totalScans} total scans</span>
              {test.startDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Started {format(new Date(test.startDate), 'MMM d')}
                </span>
              )}
            </div>
            {test.status === 'active' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewAnalytics(test.id)}
              >
                <BarChart3 className="h-3.5 w-3.5 mr-1" />
                Analytics
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
